import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BiArrowBack } from 'react-icons/bi';
import { parivartanAPI } from '../../../services/parivartanAPI';
import { platformAPI } from '../../../services/platformAPI';
import tenderAPI from '../../../services/tenderAPI';
import MasterLookup, { useMasterOptions } from '../../../components/workspace/MasterLookup';
import EvidencePicker from './EvidencePicker';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { canEditTender } from '../../../utils/tenderRecordAccess';
import { useMasterRevision } from '../../../components/workspace/useMasterRevision';
import { useAltF1TestFill } from '../../../hooks/useAltF1TestFill';
import { dummyParivartanPersonnelForm } from '../../../utils/testFormDummies';
import { loadOrganisationSettings } from '../../../utils/organisationSettings';
import './CustomerMaster.scss';
import './PersonnelPage.scss';

const DesignationField = ({ form, setForm, locked }) => {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    platformAPI.listReference('personnel-designations', { keep: form.designationCode || undefined })
      .then((response) => setOptions(response.data?.data?.values || []))
      .catch(() => setOptions([]));
  }, [form.designationCode]);
  return (
    <>
      <Form.Select
        aria-label="Designation"
        value={form.designationCode || ''}
        disabled={locked}
        onChange={(event) => {
          const code = event.target.value;
          const match = options.find((row) => row.code === code);
          setForm((current) => ({
            ...current,
            designationCode: code,
            designation: match && code !== 'other' ? match.label : current.designation
          }));
        }}
      >
        <option value="">{form.designation ? `Keep “${form.designation}”` : 'Select a designation'}</option>
        {options.filter((row) => row.status === 'active' || row.code === form.designationCode).map((row) => (
          <option key={row.code} value={row.code}>{row.label}{row.status === 'inactive' ? ' (inactive)' : ''}</option>
        ))}
      </Form.Select>
      {form.designationCode === 'other' || !form.designationCode ? (
        <Form.Control className="mt-2" value={form.designation} onChange={(event) => setForm((current) => ({ ...current, designation: event.target.value }))} disabled={locked} placeholder="Title as recorded" />
      ) : null}
      <div className="form-text">A designation is the person’s job title. The role named on a tender is chosen when the person is attached to that bid. Neither of those is an application access role.</div>
    </>
  );
};

const Field = ({ label, wide, children }) => (
  <Form.Group className={`cust-field${wide ? ` cust-field--${wide}` : ''}`}>
    <Form.Label>{label}</Form.Label>
    {children}
  </Form.Group>
);

const EMPTY = {
  name: '',
  designation: '',
  designationCode: '',
  associationStatus: '',
  qualificationsText: '',
  classification: 'employee',
  cvVersion: '1.0',
  experienceSummary: '',
  sectorTags: [],
  experienceProjectIds: [],
  availabilityFrom: '',
  availabilityTo: '',
  isActive: true,
  userId: ''
};

const monogram = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  return (parts[0] || 'New').slice(0, 2).toUpperCase();
};

const PersonnelFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { personId } = useParams();
  const isCreate = location.pathname.endsWith('/new');
  const isEdit = location.pathname.endsWith('/edit');
  const isView = !isCreate && !isEdit;
  const masters = useMasterOptions();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const revision = useMasterRevision({ entity: 'personnel' });
  const [activeTenders, setActiveTenders] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [attachTenders, setAttachTenders] = useState([]);
  const [attachRole, setAttachRole] = useState('');
  const [types, setTypes] = useState([
    { code: 'employee', label: 'Employee' },
    { code: 'consultant', label: 'Consultant' }
  ]);
  const [cvDocuments, setCvDocuments] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  useDocumentTitle(isCreate ? 'New person' : (form.name || 'Person'));

  useEffect(() => {
    loadOrganisationSettings().then((data) => {
      const list = data?.settings?.personnelTypes;
      if (list?.length) setTypes(list);
    });
    platformAPI.listKnowledge()
      .then((response) => {
        const rows = response.data?.data?.projectExperience || [];
        setProjectOptions(rows.map((row) => ({
          value: String(row._id),
          label: row.projectName || 'Assignment',
          hint: row.client || '',
          aliases: [row.projectName, row.client].filter(Boolean)
        })));
      })
      .catch(() => setProjectOptions([]));
  }, []);

  useEffect(() => {
    if (isCreate) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await parivartanAPI.getPersonnel(personId);
        const row = response.data?.data?.personnel;
        if (!cancelled && row) {
          setForm({
            name: row.name || '',
            designation: row.designation || '',
            designationCode: row.designationCode || '',
            associationStatus: row.associationStatus || '',
            qualificationsText: (row.qualificationRecords || []).map((item) => [item.title, item.institution, item.year].filter((part) => part != null && part !== '').join(' | ')).join('\n')
              || (row.qualifications || []).join('\n'),
            classification: row.classification || 'employee',
            cvVersion: row.cvVersion || '1.0',
            experienceSummary: row.experienceSummary || '',
            sectorTags: Array.isArray(row.sectorSpecialisation) ? row.sectorSpecialisation : [],
            experienceProjectIds: (row.experienceProjectIds || []).map((id) => String(id)),
            availabilityFrom: row.availabilityFrom ? String(row.availabilityFrom).slice(0, 10) : '',
            availabilityTo: row.availabilityTo ? String(row.availabilityTo).slice(0, 10) : '',
            isActive: row.isActive !== false,
            userId: row.userId || '',
            revision: row.revision || 1
          });
          setAttachRole(row.designation || '');
          const cvIds = row.cvDocumentIds?.length ? row.cvDocumentIds : (row.cvDocumentId ? [row.cvDocumentId] : []);
          setCvDocuments(cvIds.map((id) => ({ documentId: id, name: 'CV', version: 1 })));
        }
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.message || 'Unable to load this person');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [personId, isCreate]);

  useEffect(() => {
    if (isCreate) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [tenderRes, proposalRes] = await Promise.all([
          tenderAPI.getTenders().catch(() => ({})),
          parivartanAPI.listPersonnelProposals()
        ]);
        if (cancelled) return;
        const tenderRows = tenderRes?.data?.tenders || tenderRes?.tenders || [];
        const links = proposalRes.data?.data?.proposals || [];
        setActiveTenders(tenderRows.filter(canEditTender));
        setProposals(links.filter((item) => String(item.personnelId) === String(personId)));
      } catch {
        if (!cancelled) setProposals([]);
      }
    })();
    return () => { cancelled = true; };
  }, [isCreate, personId]);

  useAltF1TestFill(isCreate || isEdit, () => {
    const dummy = dummyParivartanPersonnelForm();
    setForm({
      name: dummy.name || '',
      designation: dummy.designation || '',
      classification: dummy.classification || 'employee',
      cvVersion: dummy.cvVersion || '1.0',
      experienceSummary: dummy.experienceSummary || '',
      sectorTags: Array.isArray(dummy.sectorTags)
        ? dummy.sectorTags
        : String(dummy.sectorTags || '').split(',').map((item) => item.trim()).filter(Boolean),
      isActive: true
    });
  });

  const setField = (field) => (event) => {
    const value = field === 'isActive' ? event.target.value === 'active' : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const tenderOptions = useMemo(
    () => activeTenders
      .filter((item) => !proposals.some((link) => String(link.tenderId) === String(item._id || item.id)))
      .map((item) => ({
        value: item._id || item.id,
        label: `${item.reference || 'Tender'} — ${item.title || 'Untitled'}`,
        hint: item.organization || '',
        aliases: [item.reference, item.title, item.organization].filter(Boolean)
      })),
    [activeTenders, proposals]
  );

  const save = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    setError('');
    try {
      const sectorSpecialisation = (Array.isArray(form.sectorTags) ? form.sectorTags : [])
        .map((item) => String(item).trim())
        .filter(Boolean);
      const payload = {
        name: form.name.trim(),
        designation: form.designation.trim() || undefined,
        designationCode: form.designationCode || undefined,
        associationStatus: form.associationStatus || '',
        qualificationRecords: String(form.qualificationsText || '').split('\n').map((line) => line.trim()).filter(Boolean).map((line) => {
          const [title, institution, year] = line.split('|').map((part) => part.trim());
          return { title, institution: institution || '', year: year || '' };
        }),
        classification: form.classification,
        cvVersion: form.cvVersion.trim() || '1.0',
        experienceSummary: form.experienceSummary.trim() || undefined,
        sectorSpecialisation,
        experienceProjectIds: form.experienceProjectIds || [],
        cvDocumentIds: cvDocuments.map((item) => item.documentId).filter(Boolean),
        availabilityFrom: form.availabilityFrom || '',
        availabilityTo: form.availabilityTo || '',
        isActive: form.isActive,
        userId: form.userId || ''
      };
      if (!isCreate) {
        const changeReason = await revision.askReason(form.revision);
        if (!changeReason) return;
        payload.changeReason = changeReason;
      }
      if (isCreate) {
        const response = await parivartanAPI.upsertPersonnel(payload);
        const created = response.data?.data?.personnel;
        const id = created?._id || created?.id;
        navigate(id ? `/apps/tender/personnel/${id}/edit` : '/apps/tender/personnel');
      } else {
        await parivartanAPI.upsertPersonnel({ ...payload, id: personId });
        navigate('/apps/tender/personnel');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Could not save this person');
    } finally {
      setSaving(false);
    }
  };

  const attach = async () => {
    if (!personId || !attachTenders.length) {
      setError('Choose at least one active tender.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await parivartanAPI.attachPersonnelToTenders({
        personnelId: personId,
        tenderIds: attachTenders,
        proposedRole: attachRole.trim() || form.designation.trim() || undefined
      });
      setAttachTenders([]);
      const next = await parivartanAPI.listPersonnelProposals();
      const links = next.data?.data?.proposals || [];
      setProposals(links.filter((item) => String(item.personnelId) === String(personId)));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not name this person on the tender');
    } finally {
      setSaving(false);
    }
  };

  const detach = async (id) => {
    setSaving(true);
    setError('');
    try {
      await parivartanAPI.detachPersonnelProposal(id);
      setProposals((items) => items.filter((item) => String(item._id) !== String(id)));
    } catch (err) {
      setError(err.response?.data?.message || 'Could not remove this person from the tender');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner animation="border" size="sm" />;

  const locked = isView;
  const heading = isCreate ? 'New person' : (form.name || 'Person');
  const shownTypes = types.some((row) => row.code === form.classification)
    ? types
    : [...types, { code: form.classification, label: form.classification }];
  const typeLabel = shownTypes.find((row) => row.code === form.classification)?.label || 'Employee';

  return (
    <div className="cust-page">
      <form className={`cust-record pers-record${locked ? ' cust-record--view' : ''}`} onSubmit={save}>
        <header className="cust-mast">
          <div className="cust-mast__identity">
            <button type="button" className="cust-page__back" onClick={() => navigate('/apps/tender/personnel')}>
              <BiArrowBack />
              Personnel
            </button>
            <div className="cust-mast__row">
              <span className="cust-mark" aria-hidden>{monogram(form.name)}</span>
              <div className="cust-mast__copy">
                <p className="cust-mast__eyebrow">{isCreate ? 'Personnel' : isEdit ? 'Editing person' : 'Personnel'}</p>
                <h1>{heading}</h1>
                <div className="cust-mast__meta">
                  <span className="cust-chip">{typeLabel}</span>
                  {form.designation ? <span className="cust-chip">{form.designation}</span> : null}
                  <span className={`cust-status cust-status--${form.isActive ? 'active' : 'inactive'}`}>
                    {form.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="cust-mast__actions">
            {isView ? (
              <Button size="sm" variant="primary" onClick={() => navigate(`/apps/tender/personnel/${personId}/edit`)}>
                Edit
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline-primary" type="button" onClick={() => navigate('/apps/tender/personnel')} disabled={saving}>
                  Cancel
                </Button>
                <Button size="sm" variant="primary" type="submit" disabled={saving || !form.name.trim()}>
                  {saving ? 'Saving…' : isCreate ? 'Create person' : 'Save changes'}
                </Button>
              </>
            )}
          </div>
        </header>

        {error ? <Alert variant="danger" className="cust-record__alert">{error}</Alert> : null}

        <div className="cust-record__body">
          <div className="cust-record__main">
            <section className="cust-section">
              <header className="cust-section__head">
                <span>01</span>
                <div>
                  <h2>Profile</h2>
                  <p>Who this person is on the firm roster. Saving here does not put them on a tender.</p>
                </div>
              </header>
              <div className="pers-sheet">
                <Field label="Full name">
                  <Form.Control value={form.name} onChange={setField('name')} required disabled={locked} placeholder="Priya Sharma" />
                </Field>
                <div className="pers-choices">
                  <div className="pers-choice">
                    <span className="pers-choice__label">Type</span>
                    <div className="pers-choice__row">
                      {shownTypes.map(({ code: value, label }) => (
                        <button
                          key={value}
                          type="button"
                          className={`pers-pick${form.classification === value ? ' is-on' : ''}`}
                          aria-pressed={form.classification === value}
                          disabled={locked}
                          onClick={() => setForm((current) => ({ ...current, classification: value }))}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="pers-choice">
                    <span className="pers-choice__label">Status</span>
                    <div className="pers-choice__row">
                      <button
                        type="button"
                        className={`pers-pick pers-pick--live${form.isActive ? ' is-on' : ''}`}
                        aria-pressed={form.isActive}
                        disabled={locked}
                        onClick={() => setForm((current) => ({ ...current, isActive: true }))}
                      >
                        Active
                      </button>
                      <button
                        type="button"
                        className={`pers-pick pers-pick--quiet${!form.isActive ? ' is-on' : ''}`}
                        aria-pressed={!form.isActive}
                        disabled={locked}
                        onClick={() => setForm((current) => ({ ...current, isActive: false }))}
                      >
                        Inactive
                      </button>
                    </div>
                  </div>
                </div>
                <div className="pers-facts">
                  <Field label="Designation">
                    <DesignationField form={form} setForm={setForm} locked={locked} />
                  </Field>
                  <Field label="Association">
                    <Form.Select value={form.associationStatus || ''} onChange={setField('associationStatus')} disabled={locked}>
                      <option value="">Not stated</option>
                      <option value="current">Current</option>
                      <option value="former">Former</option>
                    </Form.Select>
                  </Field>
                  <Field label="CV version">
                    <Form.Control value={form.cvVersion} onChange={setField('cvVersion')} disabled={locked} />
                  </Field>
                  <Field label="Workspace user">
                    <Form.Control value={form.userId} onChange={setField('userId')} disabled={locked} placeholder="User id linked to this person" />
                  </Field>
                </div>
                <Field label="Sectors">
                  <MasterLookup
                    multiple
                    options={masters.sectors}
                    value={form.sectorTags}
                    onChange={(sectorTags) => setForm((prev) => ({ ...prev, sectorTags }))}
                    placeholder="Search sectors"
                    disabled={locked || !masters.ready}
                  />
                </Field>
                <Field label="Relevant assignments">
                  <MasterLookup
                    multiple
                    options={projectOptions}
                    value={form.experienceProjectIds || []}
                    onChange={(experienceProjectIds) => setForm((prev) => ({ ...prev, experienceProjectIds }))}
                    placeholder="Search assignment experience"
                    disabled={locked}
                  />
                </Field>
                <Field label="Available from">
                  <Form.Control type="date" value={form.availabilityFrom || ''} onChange={setField('availabilityFrom')} disabled={locked} />
                </Field>
                <Field label="Available to">
                  <Form.Control type="date" value={form.availabilityTo || ''} onChange={setField('availabilityTo')} disabled={locked} />
                </Field>
                <Field label="Qualifications">
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={form.qualificationsText || ''}
                    onChange={setField('qualificationsText')}
                    disabled={locked}
                    placeholder={'One per line: BE Civil | COEP | 2012'}
                  />
                </Field>
                <Field label="Experience summary">
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={form.experienceSummary}
                    onChange={setField('experienceSummary')}
                    disabled={locked}
                    placeholder="Short narrative for the people assembling a technical response"
                  />
                </Field>
                <div className="cust-field cust-field--12">
                  <EvidencePicker
                    area="knowledge"
                    label="CV files"
                    classification="restricted"
                    documents={cvDocuments}
                    onChange={locked ? () => {} : setCvDocuments}
                  />
                </div>
              </div>
            </section>

            <section className="cust-section">
              <header className="cust-section__head">
                <span>02</span>
                <div>
                  <h2>Named on tenders</h2>
                  <p>Open pursuits where this person should appear. Submitted, awarded, and lost tenders stay off this list.</p>
                </div>
              </header>
              {isCreate ? (
                <p className="text-muted mb-0">Save the person first. You can name them on tenders from the edit page.</p>
              ) : (
                <>
                  {proposals.length ? (
                    <ul className="pers-named">
                      {proposals.map((item) => (
                        <li key={item._id}>
                          <span>
                            <strong>{item.tenderReference || 'Tender'}</strong>
                            {item.tenderTitle ? ` — ${item.tenderTitle}` : ''}
                            {item.proposedRole ? <em>{item.proposedRole}</em> : null}
                          </span>
                          {isEdit ? (
                            <button type="button" onClick={() => detach(item._id)} disabled={saving}>Remove</button>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-muted">This person is not named on an active tender yet.</p>
                  )}
                  {isEdit ? (
                    <div className="cust-fields pers-named__add">
                      <Field label="Role on the bid" wide="4">
                        <Form.Control
                          value={attachRole}
                          onChange={(event) => setAttachRole(event.target.value)}
                          placeholder={form.designation || 'Role on the bid'}
                        />
                      </Field>
                      <Field label="Active tenders" wide="8">
                        <MasterLookup
                          multiple
                          options={tenderOptions}
                          value={attachTenders}
                          onChange={setAttachTenders}
                          placeholder={tenderOptions.length ? 'Search open pursuits' : 'No further active tenders'}
                          disabled={!tenderOptions.length}
                        />
                      </Field>
                      <div className="cust-field cust-field--12">
                        <Button type="button" size="sm" variant="primary" onClick={attach} disabled={saving || !attachTenders.length}>
                          Name on selected tenders
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </section>
          </div>
        </div>
      </form>
      {revision.modals}
    </div>
  );
};

export default PersonnelFormPage;
